import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Keeps a component mounted during an exit transition after `visible` becomes false.
 * Returns `shouldRender` (whether to render at all) and `exiting` (whether the exit
 * transition is in progress).
 *
 * Two modes:
 * - **Event-driven** (default): call `onTransitionEnd` when the CSS transition completes.
 * - **Timer-driven**: pass `duration` in ms to auto-complete after a fixed delay.
 *
 * @example Event-driven (CSS transition)
 * ```tsx
 * const { shouldRender, exiting, onTransitionEnd } = useExitTransition(visible);
 * if (!shouldRender) return null;
 * return (
 *     <div
 *         className={exiting ? "scale-0" : "scale-100"}
 *         onTransitionEnd={onTransitionEnd}
 *     >
 *         Content
 *     </div>
 * );
 * ```
 *
 * @example Timer-driven (e.g. keeping a dropdown anchor visible during exit animation)
 * ```tsx
 * const { shouldRender } = useExitTransition(isMenuOpen, { duration: 150 });
 * // shouldRender stays true for 150ms after isMenuOpen becomes false
 * ```
 */
export function useExitTransition(visible: boolean, options?: { duration?: number }) {
    const [rendered, setRendered] = useState(visible);
    const [exiting, setExiting] = useState(false);
    const prevVisible = useRef(false);

    useEffect(() => {
        if (visible) {
            setRendered(true);
        } else if (prevVisible.current) {
            setExiting(true);
        }
        prevVisible.current = visible;
        // rendered must be in the dep array: the extra effect run after
        // setRendered(true) ensures prevVisible.current is set to true before
        // a dismiss can flip visible to false, so the true→false transition
        // is always detected.
    }, [visible, rendered]);

    // Timer-driven exit: auto-complete after `duration` ms.
    const duration = options?.duration;
    useEffect(() => {
        if (!exiting || duration == null) return;
        const id = setTimeout(() => {
            setRendered(false);
            setExiting(false);
        }, duration);
        return () => clearTimeout(id);
    }, [exiting, duration]);

    const onTransitionEnd = useCallback((_e?: unknown) => {
        setRendered(false);
        setExiting(false);
    }, []);

    return { shouldRender: visible || rendered, exiting, onTransitionEnd };
}
